import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * A queue of states, each held long enough to be seen.
 *
 * Two problems this solves, both real and both invisible until you look:
 *
 * 1. A tool that returns in 40 ms sets `working` and clears it before the
 *    avatar finishes a single squash. The state exists in the machine and never
 *    once exists for the viewer.
 *
 * 2. More subtly — React 18 batches updates. `set('working')` immediately
 *    followed by `set('thinking')` collapses into ONE render, so a hook that
 *    derives from a rendered prop can never observe the intermediate value. It
 *    was never rendered. Deriving is therefore the wrong shape entirely; the
 *    transitions have to be pushed as they happen.
 *
 * So this returns a `push` you call from the event, not a value you watch.
 * Every pushed state is shown for at least `ms`, in order.
 */
export function useDwellQueue<T>(
    initial: T,
    ms = 750,
    maxQueue = 5,
): [T, (next: T) => void, (value: T) => void] {
    const [shown, setShown] = useState<T>(initial);
    const queue = useRef<T[]>([]);
    const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    const shownRef = useRef<T>(initial);
    shownRef.current = shown;

    const drain = useCallback(() => {
        const next = queue.current.shift();
        if (next === undefined) { timer.current = undefined; return; }
        setShown(next);
        timer.current = setTimeout(drain, ms);
    }, [ms]);

    /** Enqueue a state. It will be shown for at least `ms`. */
    const push = useCallback((next: T) => {
        const tail = queue.current.length
            ? queue.current[queue.current.length - 1]
            : shownRef.current;
        if (Object.is(tail, next)) return;          // already heading there

        queue.current.push(next);
        if (queue.current.length > maxQueue) {
            // Keep the oldest (the sequence still starts where it started) and
            // the newest (we converge on the truth) under pressure.
            queue.current = [queue.current[0], queue.current[queue.current.length - 1]];
        }
        if (timer.current === undefined) drain();
    }, [drain, maxQueue]);

    /** Jump straight to a value, dropping anything pending. */
    const reset = useCallback((value: T) => {
        queue.current = [];
        clearTimeout(timer.current);
        timer.current = undefined;
        setShown(value);
    }, []);

    useEffect(() => () => clearTimeout(timer.current), []);

    return [shown, push, reset];
}
